import { autoinject, transient } from "aurelia-dependency-injection";
import { type OfferingsSelectionConfigModel, OnboardingConfigModel, OfferingSelectedInConfigModel } from "../../../../../../models/onboarding/onboarding.models";
import { QueryBuilder } from "../../../../../components/queries/query-builder";
import { computedFrom } from "aurelia-binding";
import { Reflection } from "../../../../../../utilities/reflection";
import { NotificationClientService } from "../../../../../../services/basics/notification-client.service";
import { ChildComponent } from "../../../../modules/child-component";
import { I18N } from "aurelia-i18n";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { Guid } from "../../../../../../utilities/guid";
import { ArrayUtils } from "../../../../../../utilities/arrays";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { useView } from "aurelia-framework";

@autoinject()
@transient()
@useView('views/pages/admin/onboarding/steps/offerings-criteria-config/offerings-criteria-config.html')
export class OfferingsSelectionConfigsManagement extends ChildComponent<OnboardingConfigModel>{

    private _selectedOfferingsSelectionConfig: OfferingsSelectionConfigModel;
    private _searchOfferingsCriteriaConfig: string;

    public basicMetaIdentifier: string;
    public offerings: OfferingSelectedInConfigModel[] = [];
    public selectedOfferings: OfferingSelectedInConfigModel[] = [];
    public queryBuilder: QueryBuilder;
    public configsFiltered: OfferingsSelectionConfigModel[] = [];
    public dataForQueryBuilder: any = {};

    public set selectedOfferingsSelectionConfig(value: OfferingsSelectionConfigModel) {
        this._selectedOfferingsSelectionConfig = value;
        if (this._selectedOfferingsSelectionConfig) {
            this._selectedOfferingsSelectionConfig.queries = value && value.queryBody
                && value.queryBody.length > 0 ? JSON.parse(value.queryBody) : [];
            if (!this._selectedOfferingsSelectionConfig.offeringsSelected) {
                this._selectedOfferingsSelectionConfig.offeringsSelected = [];
            }
            this.offerings = this.item.offerings.filter(x => x.isActive).map(x => ({
                offeringId: x.offeringId,
                offeringsSelectionConfigId: this.selectedOfferingsSelectionConfig.offeringsSelectionConfigId,
                offeringSelectedInConfigId: 0
            }));
            this.onOfferingSelected();
            setTimeout(() => {
                if (this._selectedOfferingsSelectionConfig.queries.length == 0) {
                    this.queryBuilder.addFilter();
                }
            });
        }
    }

    @computedFrom(Reflection.getPropertyPath<OfferingsSelectionConfigsManagement>(x => x._selectedOfferingsSelectionConfig))
    public get selectedOfferingsSelectionConfig(): OfferingsSelectionConfigModel {
        return this._selectedOfferingsSelectionConfig;
    }

    public set searchOfferingsCriteriaConfig(value: string) {
        this._searchOfferingsCriteriaConfig = value;
        let searchOfferingsSelectionConfigsFiltered = [];
        if (value) {
            searchOfferingsSelectionConfigsFiltered = this.item.offeringsSelectionConfigs.filter(x => x.configName.toLowerCase().indexOf(value.toLowerCase()) > -1);
        } else {
            searchOfferingsSelectionConfigsFiltered = this.item.offeringsSelectionConfigs;
        }
        this.configsFiltered = searchOfferingsSelectionConfigsFiltered;
    }

    @computedFrom(Reflection.getPropertyPath<OfferingsSelectionConfigsManagement>(x => x._searchOfferingsCriteriaConfig))
    public get searchOfferingsCriteriaConfig(): string {
        return this._searchOfferingsCriteriaConfig;
    }

    public constructor(private userClientService: UserClientService,
        notificationService: NotificationClientService,
        i18N: I18N) {
        super(notificationService, i18N)
    }

    public deleteOfferingSelected(selectedOffering: OfferingSelectedInConfigModel) {
        const index = ArrayUtils.findIndex(this._selectedOfferingsSelectionConfig.offeringsSelected, x => x == selectedOffering);
        if (index > -1) {
            this._selectedOfferingsSelectionConfig.offeringsSelected.splice(index, 1);
            this.onOfferingSelected();
        }
    }

    public onOfferingsSelectionConfigSelected(offeringsSelectionConfig: OfferingsSelectionConfigModel): void {
        this.selectedOfferingsSelectionConfig = offeringsSelectionConfig;
    }

    public newOfferingsSelectionConfig(): void {

        const offeringsSelectionConfig = <OfferingsSelectionConfigModel>{
            onboardingConfigId: this.item.onboardingConfigId,
            createdById: this.userClientService.currentUser.id,
            dateCreated: new Date(),
            guid: Guid.generateGuid(),
            identifier: Guid.generateGuid(),
            isActive: true,
            isSharedGlobally: false,
            configName: "",
            queries: [],
            offeringsSelectionConfigId: 0,
            queryBody: "",
            entityType: EntityTypes.OfferingSelectionConfig
        };
        this.selectedOfferingsSelectionConfig = offeringsSelectionConfig;
        if (!this.item.offeringsSelectionConfigs) {
            this.item.offeringsSelectionConfigs = [];
        }
        this.item.offeringsSelectionConfigs.push(offeringsSelectionConfig);
    }

    addSelectedOffering() {
        this.selectedOfferingsSelectionConfig.offeringsSelected.push({
            offeringId: this.item.offerings.filter(x => this.selectedOfferings.every(z => z.offeringId != x.offeringId))[0].offeringId,
            offeringsSelectionConfigId: this.selectedOfferingsSelectionConfig.offeringsSelectionConfigId,
            offeringSelectedInConfigId: 0
        });
        this.onOfferingSelected();
    }

    onOfferingSelected() {
        this.selectedOfferings = this.offerings.filter(x => this.selectedOfferingsSelectionConfig.offeringsSelected.some(z => z.offeringId == x.offeringId));
    }

    deleteConfig(config: OfferingsSelectionConfigModel) {
        const index = this.item.offeringsSelectionConfigs.indexOf(config);
        if (index > -1) {
            if (this.item.offeringsSelectionConfigs[index] == this.selectedOfferingsSelectionConfig) {
                this.selectedOfferingsSelectionConfig = null;
            }
            this.item.offeringsSelectionConfigs.splice(index, 1);
        }
    }

    public cancel(): void {
        this.selectedOfferingsSelectionConfig = null;
    }

    public getOfferingName = (item: OfferingSelectedInConfigModel) => {
        return item ? this.item.offerings.filter(x => x.offeringId == item.offeringId)[0].name : '';
    }

    activate(model: { item: OnboardingConfigModel, basicMetaIdentifier: string }) {
        super.activate(model.item);
        this.basicMetaIdentifier = model.basicMetaIdentifier;
        this.configsFiltered = this.item.offeringsSelectionConfigs;
        if (this.selectedOfferingsSelectionConfig) {
            this.selectedOfferingsSelectionConfig = this.item.offeringsSelectionConfigs.filter(x => x.guid == this.selectedOfferingsSelectionConfig.guid)[0];
        } else {
            this.selectedOfferingsSelectionConfig = null;
        }
    }
}
